import type { SiteContactData } from '../services/copSiteDetails.service'
import type { ContactFieldConfig, ContactFormState } from './EditContactsModal.types'

export const CONTACT_KEYS_ALLOWED: (keyof SiteContactData)[] = [
    'salutation', 'firstName', 'lastName', 'email', 'phone', 'role',
]

// Split-based validator avoids regex backtracking risk.
export const isValidEmail = (v: string): boolean => {
    const parts = v.split('@')
    if (parts.length !== 2) return false
    const [local, domain] = parts
    const domainParts = domain.split('.')
    return local.length > 0 && domainParts.length >= 2 && domainParts.every(p => p.length > 0)
}

export const isValidPhone = (v: string): boolean => /^[+\d][\d\s\-().]{4,18}$/.test(v)

// The dialog's Field Type select stores 'tel' for the phone option; treat both as equivalent.
export const isPhoneFieldType = (fieldType: string): boolean => fieldType === 'phone' || fieldType === 'tel'

const normalizeKey = (k: string): string => k.trim().toLowerCase().replace(/[\s_-]+/g, '')

// Bridges common authoring labels to the canonical API property name.
const FIELD_KEY_ALIASES: Record<string, keyof SiteContactData> = {
    jobfunction: 'role',
    jobtitle: 'role',
    role: 'role',
    emailaddress: 'email',
    emailid: 'email',
    email: 'email',
    phonenumber: 'phone',
    telephone: 'phone',
    tel: 'phone',
    phone: 'phone',
    firstname: 'firstName',
    givenname: 'firstName',
    lastname: 'lastName',
    surname: 'lastName',
    familyname: 'lastName',
    title: 'salutation',
    salutation: 'salutation',
}

// Case-insensitive/trimmed lookup so slight authoring differences (e.g., "FirstName" vs "firstName") still resolve.
function buildLookup(contact: SiteContactData | null | undefined): Record<string, string> {
    if (!contact) return {}
    const lookup: Record<string, string> = {}
    for (const [k, v] of Object.entries(contact)) {
        lookup[normalizeKey(k)] = v == null ? '' : String(v)
    }
    return lookup
}

function resolveContactValue(
    lookup: Record<string, string>,
    fieldKey: string,
): string {
    const normalized = normalizeKey(fieldKey)
    if (normalized in lookup) return lookup[normalized]
    const aliased = FIELD_KEY_ALIASES[normalized]
    if (aliased && normalizeKey(aliased) in lookup) return lookup[normalizeKey(aliased)]
    return ''
}

export function toFormState(
    contact: SiteContactData | null | undefined,
    fields: ContactFieldConfig[] = [],
): ContactFormState {
    const lookup = buildLookup(contact)
    const form: ContactFormState = {}
    // Prepopulate every configured field so the render lookup by `field.fieldKey` always resolves.
    for (const field of fields) {
        form[field.fieldKey] = resolveContactValue(lookup, field.fieldKey)
    }
    // Preserve any additional API-supplied keys so save-time payload construction still works.
    if (contact) {
        for (const [k, v] of Object.entries(contact)) {
            if (!(k in form)) form[k] = v == null ? '' : String(v)
        }
    }
    return form
}

type ValidateSideArgs = {
    side: 'primary' | 'secondary'
    fields: ContactFieldConfig[]
    formState: ContactFormState
    emailMsg: string
    phoneMsg: string
}

function validateSide({ side, fields, formState, emailMsg, phoneMsg }: ValidateSideArgs): Record<string, string> {
    const errors: Record<string, string> = {}
    for (const field of fields) {
        const val = formState[field.fieldKey]?.trim() ?? ''
        if (!val) continue
        const isInvalid = (field.fieldType === 'email' && !isValidEmail(val))
            || (isPhoneFieldType(field.fieldType) && !isValidPhone(val))
        if (!isInvalid) continue
        const template = field.fieldType === 'email' ? emailMsg : phoneMsg
        errors[`${side}_${field.fieldKey}`] = template.replace('{field}', field.fieldLabel)
    }
    return errors
}

type ValidateFieldsArgs = {
    fields: ContactFieldConfig[]
    primary: ContactFormState
    secondary: ContactFormState
    emailInvalidMsg: string
    phoneInvalidMsg: string
}

export function validateFields({
    fields, primary, secondary, emailInvalidMsg, phoneInvalidMsg,
}: ValidateFieldsArgs): Record<string, string> {
    return {
        ...validateSide({ side: 'primary', fields, formState: primary, emailMsg: emailInvalidMsg, phoneMsg: phoneInvalidMsg }),
        ...validateSide({ side: 'secondary', fields, formState: secondary, emailMsg: emailInvalidMsg, phoneMsg: phoneInvalidMsg }),
    }
}

// Maps each configured fieldKey to the canonical API property, when resolvable.
export function resolveFieldKeyToApiKey(fieldKey: string): keyof SiteContactData | null {
    const normalized = normalizeKey(fieldKey)
    if ((CONTACT_KEYS_ALLOWED as string[]).map(normalizeKey).includes(normalized)) {
        return (CONTACT_KEYS_ALLOWED as string[]).find(k => normalizeKey(k) === normalized) as keyof SiteContactData
    }
    return FIELD_KEY_ALIASES[normalized] ?? null
}

export function buildContactPayload(
    form: ContactFormState,
    orig: SiteContactData | null | undefined,
    fields: ContactFieldConfig[],
): SiteContactData {
    const editableApiKeys = new Map<keyof SiteContactData, string>()
    for (const field of fields) {
        const apiKey = resolveFieldKeyToApiKey(field.fieldKey)
        if (apiKey) editableApiKeys.set(apiKey, field.fieldKey)
    }
    const out: Record<string, string | null> = {}
    for (const k of CONTACT_KEYS_ALLOWED) {
        const formKey = editableApiKeys.get(k)
        if (formKey !== undefined) {
            out[k] = form[formKey]?.trim() || null
        } else {
            out[k] = orig?.[k] ?? null
        }
    }
    return out as SiteContactData
}
